import { Box, Grid, Paper, Skeleton, Stack, Typography, Divider } from "@mui/material";
import { useState } from "react";
import { useSelector } from "react-redux";
import "../../assets/styles/plans.css";
import { PlanCheckout } from "../PlanCheckout";
import { PlanCheckoutError } from "../PlanCheckoutError";
import { PlanCheckoutSuccess } from "../PlanCheckoutSuccess";
import { PlanCheckoutTicket } from "../PlanCheckoutTicket";

const planCheckoutComponentMap = {
  success: PlanCheckoutSuccess,
  error: PlanCheckoutError,
};

export const PlanCheckoutLayout = () => {
  const { planSelected } = useSelector((state) => state.planState);

  const [subscriptionState, setSubscriptionState] = useState({
    state: "none",
    data: {
      message: "",
      subscriptionId: null,
      orderId: null,
    },
  });

  const isCheckoutSuccess = subscriptionState?.state === "success";
  const PlanCheckoutComponent = planCheckoutComponentMap[subscriptionState?.state] || PlanCheckout;

  if (!planSelected && subscriptionState.state === "none") {
    return (
      <Box
        className="planLayout"
        sx={{
          width: "100%",
          minHeight: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          p: { xs: 2, md: 4 },
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1100 }}>
          <Stack spacing={4}>
            {/* Ticket Skeleton */}
            <Box sx={{ p: 3, borderRadius: 2.5, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
              <Skeleton variant="text" width="160px" height={32} />
              <Skeleton variant="text" width="280px" height={24} sx={{ mt: 1 }} />
              <Stack spacing={1} sx={{ mt: 2 }}>
                <Skeleton variant="rectangular" height={20} sx={{ borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={20} sx={{ borderRadius: 1 }} />
              </Stack>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1.1fr 0.9fr" },
                gap: 2.5,
              }}
            >
              <Box sx={{ p: 3, borderRadius: 2.5, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
                <Skeleton variant="text" width="140px" height={32} />
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Skeleton variant="text" height={24} />
                  <Skeleton variant="text" height={24} />
                  <Skeleton variant="text" height={24} />
                </Stack>
              </Box>

              <Box sx={{ p: 3, borderRadius: 2.5, bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}>
                <Skeleton variant="text" width="100px" height={32} />
                <Stack spacing={1.5} sx={{ mt: 2 }}>
                  <Skeleton variant="text" height={24} />
                  <Divider />
                  <Skeleton variant="rectangular" height={48} sx={{ borderRadius: 2, bgcolor: "rgba(251, 142, 0, 0.12)" }} />
                </Stack>
              </Box>
            </Box>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      className="planLayout"
      sx={{
        width: "100%",
        minHeight: "100%",
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        animation: "fadeIn 1.5s",
        "@keyframes fadeIn": {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
      }}
    >
      <Grid
        container
        spacing={4}
        className={subscriptionState.state !== "none" ? "successCheckout" : "checkout"}
        sx={{
          width: "100%",
          minHeight: "100%",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        {/* 1. Summary / Items */}
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            order: 1,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: {
                xs: "100%",
                md: 1100,
              },
            }}
          >
            <PlanCheckoutTicket
              planSelected={planSelected}
              title={
                isCheckoutSuccess ? (
                  <>
                    Subscription ID: <br />
                    {subscriptionState?.data?.subscriptionId}
                  </>
                ) : (
                  <Typography
                    variant="h4"
                    sx={{
                      color: "var(--fx-surface-text, #0F172A)",
                      fontWeight: 800,
                      letterSpacing: "-0.03em",
                      mb: 0.5,
                    }}
                  >
                    Summary
                  </Typography>
                )
              }
              subscriptionState={subscriptionState}
            />
          </Box>
        </Grid>

        {/* 2. Selected + Cost / Terms / Payment */}
        <Grid
          item
          xs={12}
          sx={{
            display: "flex",
            justifyContent: "center",
            order: 2,
          }}
        >
          <Box
            sx={{
              width: "100%",
              maxWidth: {
                xs: "100%",
                md: 1100,
              },
            }}
          >
            <PlanCheckoutComponent subscriptionState={subscriptionState} setSubscriptionState={setSubscriptionState} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};
